import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Lock } from "lucide-react";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import {
  createOrUpdateTreatmentPlan,
  AlignerDetails,
  BracesDetails,
  TreatmentPlanData,
  TreatmentPlanPrescription,
  getTreatmentPlanById,
  deleteIprImage,
} from "../../api/treatmentPlan";
import { getAllPatients } from "../../api/patient";
import {
  getAllPrescriptions,
  Prescription,
} from "../../api/prescription";
import ConfirmModal from "../../components/ConfirmModal";

const initialAlignerDetails: AlignerDetails = {
  daysToWearEachAligner: 7, // Default to a valid option
  recommendedDailyHours: 0,
  upperJawOptions: [],
  lowerJawOptions: [],
  prescriptions: [],
};

const initialBracesDetails: BracesDetails = {
  notes: "",
  bracketType: "",
  anchorageUpperJaw: [],
  extractLowerJawTeeth: [],
  extractUpperJawTeeth: [],
  anchorageLowerJaw: [],
  durationInMonths: "",
  bracketMaterial: "",
  startDate: "",
  treatmentType: "",
  bracketSubType: "",
  prescriptions: [],
};

// Tooth numbers options (now 1–50 for both jaws)
const UPPER_TEETH_VALUES = Array.from({ length: 50 }, (_, idx) => idx + 1);
const LOWER_TEETH_VALUES = Array.from({ length: 50 }, (_, idx) => idx + 1);

const UPPER_TEETH_OPTIONS = UPPER_TEETH_VALUES.map((value) => ({
  value: String(value),
  label: String(value),
}));

const LOWER_TEETH_OPTIONS = LOWER_TEETH_VALUES.map((value) => ({
  value: String(value),
  label: String(value),
}));

// Dental examination type options
const DENTAL_EXAMINATION_OPTIONS = [
  { label: "Child", value: "child" },
  { label: "Adult", value: "adult" },
];

// Child teeth options
const CHILD_UPPER_TEETH = [51, 52, 53, 54, 55, 61, 62, 63, 64, 65];
const CHILD_LOWER_TEETH = [71, 72, 73, 74, 75, 81, 82, 83, 84, 85];

// Adult teeth options
const ADULT_UPPER_TEETH = [11, 12, 13, 14, 15, 16, 17, 18, 21, 22, 23, 24, 25, 26, 27, 28];
const ADULT_LOWER_TEETH = [31, 32, 33, 34, 35, 36, 37, 38, 41, 42, 43, 44, 45, 46, 47, 48];

const BRACES_DURATION_OPTIONS = ["6-8", "10-12", "12-18", "18-24", "24"];

// Anchorage enums (single-select) for braces
const DEFAULT_ANCHORAGE_OPTIONS: OptionItem[] = [
  { label: "Banding 2nd molar", value: "BANDING_2ND_MOLAR" },
  { label: "TPA", value: "TPA" },
  { label: "Nache patalal arch", value: "NANCE_PALATAL_ARCH" },
];

const BRACKET_TYPE_OPTIONS = [
  { label: "Metal", value: "METAL" },
  { label: "Aesthetic", value: "AESTHETIC" },
];

const BRACKET_MATERIAL_OPTIONS = [
  { label: "Pre adjusted", value: "PRE_ADJUSTED" },
  { label: "Self ligating", value: "SELF_LIGATING" },
  { label: "Lingual", value: "LINGUAL" },
  { label: "Damon", value: "DAMON" },
];

const BRACKET_SUBTYPE_OPTIONS = [
  { label: "0.018", value: "0.018" },
  { label: "0.022", value: "0.022" },
];


const toNumberArray = (input: unknown): number[] => {
  if (Array.isArray(input)) {
    return input
      .map((item) => {
        const value = Number(item);
        return Number.isFinite(value) ? value : null;
      })
      .filter((value): value is number => value !== null);
  }
  if (typeof input === "string") {
    return input
      .split(",")
      .map((segment) => Number(segment.trim()))
      .filter((value) => Number.isFinite(value));
  }
  return [];
};

const toStringArray = (input: unknown): string[] => {
  if (Array.isArray(input)) {
    return input
      .map((item) => String(item).trim())
      .filter((value) => value.length > 0);
  }
  if (typeof input === "string") {
    return input
      .split(",")
      .map((segment) => segment.trim())
      .filter((value) => value.length > 0);
  }
  return [];
};

const normalizePrescriptions = (
  entries: unknown
): TreatmentPlanPrescription[] => {
  if (!Array.isArray(entries)) return [];
  return entries
    .map((entry) => {
      const record = entry as Partial<TreatmentPlanPrescription> & {
        id?: string;
        _id?: string;
        medications?: unknown;
      };
      const prescriptionId =
        record?.prescriptionId || record?.id || record?._id || "";
      const medications = toStringArray(record?.medications);
      return {
        prescriptionId,
        medications,
      };
    })
    .filter(
      (entry) =>
        entry.prescriptionId.length > 0 || entry.medications.length > 0
    );
};

const normalizeAlignerDetails = (
  details?: Partial<AlignerDetails> | null
): AlignerDetails => ({

  daysToWearEachAligner:
    Number(details?.daysToWearEachAligner) ||
    initialAlignerDetails.daysToWearEachAligner,
  recommendedDailyHours: Number(details?.recommendedDailyHours) || 0,
  upperJawOptions: toNumberArray(details?.upperJawOptions),
  lowerJawOptions: toNumberArray(details?.lowerJawOptions),
  prescriptions: normalizePrescriptions(details?.prescriptions),
});

const normalizeBracesDetails = (
  details?: Partial<BracesDetails> | null
): BracesDetails => ({
  notes: details?.notes ?? "",
  // Normalize bracketType to backend enum values METAL | AESTHETIC
  // Backend sometimes swaps bracketType & bracketMaterial, so we defensively check both
  bracketType: (() => {
    const rawType = (details?.bracketType ?? "").toString().toUpperCase().trim();
    const rawMaterial = (details?.bracketMaterial ?? "")
      .toString()
      .toUpperCase()
      .trim();

    const isType = (val: string) => val === "METAL" || val === "AESTHETIC";

    if (isType(rawType)) return rawType;
    if (isType(rawMaterial)) return rawMaterial;
    return "";
  })(),
  anchorageUpperJaw: toStringArray(details?.anchorageUpperJaw),
  extractLowerJawTeeth: toNumberArray(details?.extractLowerJawTeeth),
  extractUpperJawTeeth: toNumberArray(details?.extractUpperJawTeeth),
  anchorageLowerJaw: toStringArray(details?.anchorageLowerJaw),
  durationInMonths: (() => {
    const value = details?.durationInMonths;
    if (typeof value === "number") return String(value);
    if (typeof value === "string") return value;
    return "";
  })(),
  // Normalize bracketMaterial to backend enum values PRE_ADJUSTED | SELF_LIGATING | LINGUAL
  // Backend sometimes swaps bracketType & bracketMaterial, so we defensively check both
  bracketMaterial: (() => {
    const rawMaterial = (details?.bracketMaterial ?? "")
      .toString()
      .toUpperCase()
      .trim();
    const rawType = (details?.bracketType ?? "").toString().toUpperCase().trim();

    const isMaterial = (val: string) =>
      val === "PRE_ADJUSTED" || val === "SELF_LIGATING" || val === "LINGUAL";

    if (isMaterial(rawMaterial)) return rawMaterial;
    if (isMaterial(rawType)) return rawType;
    return "";
  })(),
  startDate: details?.startDate ?? "",
  treatmentType: details?.treatmentType ?? "",
  // Normalize bracketSubType to our dropdown values (0.018 / 0.022)
  bracketSubType: (() => {
    const raw = (details?.bracketSubType ?? "")
      .toString()
      .toUpperCase()
      .trim();
    if (raw === "0.018" || raw === "0.022") return raw;
    if (raw === "TYPE_018") return "0.018";
    if (raw === "TYPE_022") return "0.022";
    return "";
  })(),
  prescriptions: normalizePrescriptions(details?.prescriptions),
});

const unwrapPlanPayload = (payload: unknown) => {
  const source = payload as any;
  return source?.data?.data ?? source?.data ?? source ?? null;
};

const TreatmentPlanForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  // Main state
  const [treatmentType, setTreatmentType] = useState<"ALIGNER" | "BRACES">(
    "ALIGNER"
  );
  const [patientId, setPatientId] = useState("");
  const [treatmentId, setTreatmentId] = useState("");
  const [isPreFilledFromTreatment, setIsPreFilledFromTreatment] = useState(false);
  const isInitialLoadRef = useRef(true);
  type PatientOption = { id: string; name: string; treatmentId?: string };
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [prescriptionsLoading, setPrescriptionsLoading] = useState(false);
  const [prescriptionOptions, setPrescriptionOptions] = useState<
    { id: string; name: string; medicines: string[] }[]
  >([]);
  const [errors, setErrors] = useState<{
    patientId?: string;
    treatmentId?: string;
    notes?: string;
    bracketType?: string;
    bracketMaterial?: string;
    bracketSubType?: string;
    durationInMonths?: string;
    startDate?: string;
    anchorageUpperJaw?: string;
    anchorageLowerJaw?: string;
    extractUpperJawTeeth?: string;
    extractLowerJawTeeth?: string;
  }>({});

  // State for each form type
  const [alignerDetails, setAlignerDetails] = useState<AlignerDetails>(
    initialAlignerDetails
  );
  const [bracesDetails, setBracesDetails] =
    useState<BracesDetails>(initialBracesDetails);

  const [iprFiles, setIprFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isIprModalOpen, setIsIprModalOpen] = useState(false);
  const [existingIprDetails, setExistingIprDetails] = useState<{ id: string; url: string }[]>([]);
  const [showIprDeleteConfirm, setShowIprDeleteConfirm] = useState(false);
  const [selectedIprId, setSelectedIprId] = useState<string>("");

  // Braces jaw selection states
  const [isUpperJawDropdownOpen, setIsUpperJawDropdownOpen] = useState(false);
  const [isLowerJawDropdownOpen, setIsLowerJawDropdownOpen] = useState(false);
  const upperJawDropdownRef = useRef<HTMLDivElement | null>(null);
  const lowerJawDropdownRef = useRef<HTMLDivElement | null>(null);
  const [selectedUpperJawTeeth, setSelectedUpperJawTeeth] = useState<number[]>([]);
  const [selectedLowerJawTeeth, setSelectedLowerJawTeeth] = useState<number[]>([]);

  // Aligner jaw selection states
  const [isAlignerUpperJawDropdownOpen, setIsAlignerUpperJawDropdownOpen] = useState(false);
  const [isAlignerLowerJawDropdownOpen, setIsAlignerLowerJawDropdownOpen] = useState(false);
  const alignerUpperJawDropdownRef = useRef<HTMLDivElement | null>(null);
  const alignerLowerJawDropdownRef = useRef<HTMLDivElement | null>(null);

  // Dental examination type state
  const [dentalExaminationType, setDentalExaminationType] = useState<"child" | "adult">("adult");
  const [bracesDentalExaminationType, setBracesDentalExaminationType] = useState<"child" | "adult">("adult");

  // Load patients for dropdown
  useEffect(() => {
    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const res = await getAllPatients();
        const list = res?.data?.data || res?.data || [];
        const normalized: PatientOption[] = (Array.isArray(list) ? list : [])
          .map((p: any): PatientOption => {
            const id = p.id || p._id || p.patientId || p.value || "";
            const name = p.name || p.label || p.fullName || "";
            const treatmentId =
              p.treatmentId ||
              p.treatment_id ||
              p.treatment?.id ||
              "";
            return { id, name, treatmentId };
          })
          .filter((p) => p.id && p.name);
        setPatients(normalized);
      } catch (e) {
        setPatients([] as PatientOption[]);
      } finally {
        setPatientsLoading(false);
      }
    };
    fetchPatients();
  }, []);

  useEffect(() => {
    const fetchPrescriptions = async () => {
      setPrescriptionsLoading(true);
      try {
        const list: Prescription[] = await getAllPrescriptions();
        const normalized = (Array.isArray(list) ? list : [])
          .map((p) => {
            const id = p.id || "";
            const name = p.name || "Untitled Prescription";
            const medicines = (p.medicines || []).map((m) => {
              const parts = [
                m.medicineName,
                m.dosage,
                m.frequency,
                m.durationDays ? `${m.durationDays} days` : "",
                m.instructions,
              ].filter(Boolean);
              return parts.join(" • ");
            });
            return { id, name, medicines };
          })
          .filter((p) => p.id);
        setPrescriptionOptions(normalized);
      } catch {
        setPrescriptionOptions([]);
      } finally {
        setPrescriptionsLoading(false);
      }
    };
    fetchPrescriptions();
  }, []);

  // Handle query parameters for pre-filling form when coming from treatment list
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryPatientId = searchParams.get('patientId');

    if (queryPatientId && !id) {
      // Only pre-fill if we're creating a new plan (not editing)
      setPatientId(queryPatientId);

      // Clear any validation errors since we're pre-filling valid data
      setErrors({});

      // Mark that form was pre-filled from treatment
      setIsPreFilledFromTreatment(true);
    }
  }, [location.search, id]);

  // Auto-populate treatmentId when patient is pre-filled or changed
  useEffect(() => {
    if (patientId && patients.length > 0) {
      const selectedPatient = patients.find((p) => p.id === patientId);
      if (selectedPatient?.treatmentId && selectedPatient.treatmentId !== treatmentId) {
        setTreatmentId(String(selectedPatient.treatmentId));
      }
    }
  }, [patientId, patients, treatmentId]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        upperJawDropdownRef.current &&
        !upperJawDropdownRef.current.contains(event.target as Node)
      ) {
        setIsUpperJawDropdownOpen(false);
      }
      if (
        lowerJawDropdownRef.current &&
        !lowerJawDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLowerJawDropdownOpen(false);
      }
      if (
        alignerUpperJawDropdownRef.current &&
        !alignerUpperJawDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAlignerUpperJawDropdownOpen(false);
      }
      if (
        alignerLowerJawDropdownRef.current &&
        !alignerLowerJawDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAlignerLowerJawDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle functions for tooth selection
  const toggleUpperJawTooth = (toothNumber: number) => {
    setSelectedUpperJawTeeth((prev) => {
      const exists = prev.includes(toothNumber);
      const next = exists
        ? prev.filter((num) => num !== toothNumber)
        : [...prev, toothNumber];

      // Update bracesDetails with the new selection
      setBracesDetails((prevBraces) => ({
        ...prevBraces,
        extractUpperJawTeeth: next,
      }));

      // Clear validation error
      if (errors.extractUpperJawTeeth) {
        setErrors((prev) => ({ ...prev, extractUpperJawTeeth: undefined }));
      }

      return next;
    });
  };

  const toggleLowerJawTooth = (toothNumber: number) => {
    setSelectedLowerJawTeeth((prev) => {
      const exists = prev.includes(toothNumber);
      const next = exists
        ? prev.filter((num) => num !== toothNumber)
        : [...prev, toothNumber];

      // Update bracesDetails with the new selection
      setBracesDetails((prevBraces) => ({
        ...prevBraces,
        extractLowerJawTeeth: next,
      }));

      // Clear validation error
      if (errors.extractLowerJawTeeth) {
        setErrors((prev) => ({ ...prev, extractLowerJawTeeth: undefined }));
      }

      return next;
    });
  };

  // Toggle functions for aligner tooth selection
  const toggleAlignerUpperJawTooth = (toothNumber: number) => {
    setAlignerDetails((prev) => {
      const exists = prev.upperJawOptions.includes(toothNumber);
      const next = exists
        ? prev.upperJawOptions.filter((num) => num !== toothNumber)
        : [...prev.upperJawOptions, toothNumber];

      return {
        ...prev,
        upperJawOptions: next,
      };
    });
  };

  const toggleAlignerLowerJawTooth = (toothNumber: number) => {
    setAlignerDetails((prev) => {
      const exists = prev.lowerJawOptions.includes(toothNumber);
      const next = exists
        ? prev.lowerJawOptions.filter((num) => num !== toothNumber)
        : [...prev.lowerJawOptions, toothNumber];

      return {
        ...prev,
        lowerJawOptions: next,
      };
    });
  };

  // Get teeth options based on dental examination type
  // const getUpperTeethOptions = () => {
  //   return dentalExaminationType === "child" ? CHILD_UPPER_TEETH : ADULT_UPPER_TEETH;
  // };

  // const getLowerTeethOptions = () => {
  //   return dentalExaminationType === "child" ? CHILD_LOWER_TEETH : ADULT_LOWER_TEETH;
  // };


  // Get teeth options for Braces based on dental examination type
  // const getBracesUpperTeethOptions = () => {
  //   return bracesDentalExaminationType === "child" ? CHILD_UPPER_TEETH : ADULT_UPPER_TEETH;
  // };

  // const getBracesLowerTeethOptions = () => {
  //   return bracesDentalExaminationType === "child" ? CHILD_LOWER_TEETH : ADULT_LOWER_TEETH;
  // };

  const getUpperTeethOptions = () => UPPER_TEETH_VALUES;
  const getLowerTeethOptions = () => LOWER_TEETH_VALUES;

  const getBracesUpperTeethOptions = () => UPPER_TEETH_VALUES;
  const getBracesLowerTeethOptions = () => LOWER_TEETH_VALUES;

  // Handle dental examination type change
  const handleDentalExaminationTypeChange = (type: "child" | "adult") => {
    setDentalExaminationType(type);

    // Clear selected teeth that are not in the new type's options
    const newUpperOptions = type === "child" ? CHILD_UPPER_TEETH : ADULT_UPPER_TEETH;
    const newLowerOptions = type === "child" ? CHILD_LOWER_TEETH : ADULT_LOWER_TEETH;

    setAlignerDetails((prev) => ({
      ...prev,
      upperJawOptions: prev.upperJawOptions.filter(tooth => newUpperOptions.includes(tooth)),
      lowerJawOptions: prev.lowerJawOptions.filter(tooth => newLowerOptions.includes(tooth)),
    }));
  };

  // Handle dental examination type change for Braces
  const handleBracesDentalExaminationTypeChange = (type: "child" | "adult") => {
    setBracesDentalExaminationType(type);

    // Clear selected teeth that are not in the new type's options
    const newUpperOptions = type === "child" ? CHILD_UPPER_TEETH : ADULT_UPPER_TEETH;
    const newLowerOptions = type === "child" ? CHILD_LOWER_TEETH : ADULT_LOWER_TEETH;

    setSelectedUpperJawTeeth(prev => prev.filter(tooth => newUpperOptions.includes(tooth)));
    setSelectedLowerJawTeeth(prev => prev.filter(tooth => newLowerOptions.includes(tooth)));

    setBracesDetails((prev) => ({
      ...prev,
      extractUpperJawTeeth: prev.extractUpperJawTeeth.filter(tooth => newUpperOptions.includes(tooth)),
      extractLowerJawTeeth: prev.extractLowerJawTeeth.filter(tooth => newLowerOptions.includes(tooth)),
    }));
  };

  useEffect(() => {
    if (!id) return;
    let isMounted = true;

    const fetchPlan = async () => {
      setLoading(true);
      try {
        const response = await getTreatmentPlanById(id);

        const plan = unwrapPlanPayload(response);
        if (!plan || !isMounted) return;

        const normalizedType: "ALIGNER" | "BRACES" =
          plan.type === "BRACES" ? "BRACES" : "ALIGNER";
        const normalizedPatientId =
          plan.patientId ||
          plan.patient?.id ||
          plan.patient?._id ||
          "";
        const normalizedTreatmentId =
          plan.treatmentId || plan.treatment?.id || "";

        setTreatmentType(normalizedType);
        setPatientId(normalizedPatientId ? String(normalizedPatientId) : "");
        setTreatmentId(
          normalizedTreatmentId ? String(normalizedTreatmentId) : ""
        );
        setAlignerDetails(normalizeAlignerDetails(plan.alignerDetails));
        setBracesDetails(normalizeBracesDetails(plan.bracesDetails));

        // Sync selected teeth arrays with bracesDetails
        const normalizedBraces = normalizeBracesDetails(plan.bracesDetails);
        setSelectedUpperJawTeeth(normalizedBraces.extractUpperJawTeeth || []);
        setSelectedLowerJawTeeth(normalizedBraces.extractLowerJawTeeth || []);

        // Set dental examination type from plan-level jawType
        if (plan.jawType === "child" || plan.jawType === "adult") {
          // Set for both aligner and braces based on treatment type
          if (normalizedType === "ALIGNER") {
            setDentalExaminationType(plan.jawType);
          } else {
            setBracesDentalExaminationType(plan.jawType);
          }
        } else {
          // Fallback: detect from selected teeth for braces
          if (normalizedType === "BRACES") {
            const hasChildTeeth =
              normalizedBraces.extractUpperJawTeeth.some(tooth => CHILD_UPPER_TEETH.includes(tooth)) ||
              normalizedBraces.extractLowerJawTeeth.some(tooth => CHILD_LOWER_TEETH.includes(tooth));
            const hasAdultTeeth =
              normalizedBraces.extractUpperJawTeeth.some(tooth => ADULT_UPPER_TEETH.includes(tooth)) ||
              normalizedBraces.extractLowerJawTeeth.some(tooth => ADULT_LOWER_TEETH.includes(tooth));

            if (hasChildTeeth && !hasAdultTeeth) {
              setBracesDentalExaminationType("child");
            } else {
              setBracesDentalExaminationType("adult");
            }
          } else {
            // Fallback: detect from selected teeth for aligner
            const normalizedAligner = normalizeAlignerDetails(plan.alignerDetails);
            const hasChildTeeth =
              normalizedAligner.upperJawOptions.some(tooth => CHILD_UPPER_TEETH.includes(tooth)) ||
              normalizedAligner.lowerJawOptions.some(tooth => CHILD_LOWER_TEETH.includes(tooth));
            const hasAdultTeeth =
              normalizedAligner.upperJawOptions.some(tooth => ADULT_UPPER_TEETH.includes(tooth)) ||
              normalizedAligner.lowerJawOptions.some(tooth => ADULT_LOWER_TEETH.includes(tooth));

            if (hasChildTeeth && !hasAdultTeeth) {
              setDentalExaminationType("child");
            } else {
              setDentalExaminationType("adult");
            }
          }
        }

        // Handle existing IPR details
        const iprDetails = plan.alignerDetails?.iprDetails || [];
        if (Array.isArray(iprDetails)) {
          setExistingIprDetails(iprDetails.map((item: any) => ({
            id: item.id || '',
            url: item.url || ''
          })));
        }
      } catch (err: any) {
        if (isMounted) {
          toast.error(
            err?.response?.data?.message ||
            "Failed to load treatment plan details."
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPlan();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Reset form data when treatment type changes
  useEffect(() => {
    // Only reset if not editing an existing plan (to avoid clearing data when loading)
    if (!id) {
      // Skip the first render (initial load)
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        return;
      }

      if (treatmentType === "ALIGNER") {
        // Reset braces details to initial state
        setBracesDetails(initialBracesDetails);
        setSelectedUpperJawTeeth([]);
        setSelectedLowerJawTeeth([]);

      } else if (treatmentType === "BRACES") {
        // Reset aligner details to initial state
        setAlignerDetails(initialAlignerDetails);

      }

      // Reset file uploads and previews for both types
      setIprFiles([]);
      setPreviews([]);
      setExistingIprDetails([]);
    }
  }, [treatmentType, id]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const totalImages = existingIprDetails.length + iprFiles.length + files.length;

      // Check if adding these files would exceed the 10 image limit
      if (totalImages > 10) {
        const remainingSlots = 10 - existingIprDetails.length - iprFiles.length;
        toast.error(`Maximum 10 images allowed. You can add ${remainingSlots} more image(s).`);
        return;
      }

      setIprFiles((prev) => [...prev, ...files]);
      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setPreviews((prev) => [...prev, ...newPreviews]);
    }
  };

  const handleDeleteExistingIpr = async (iprId: string) => {
    try {
      setLoading(true);
      await deleteIprImage(iprId);
      toast.success("IPR image deleted successfully");
      setExistingIprDetails((prev) => prev.filter((item) => item.id !== iprId));
      setShowIprDeleteConfirm(false);
      setSelectedIprId("");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete IPR image.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    setIprFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => {
      const newPreviews = prev.filter((_, i) => i !== index);
      URL.revokeObjectURL(previews[index]); // Clean up memory
      return newPreviews;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: {
      patientId?: string;
      treatmentId?: string;
      notes?: string;
      bracketType?: string;
      bracketMaterial?: string;
      bracketSubType?: string;
      durationInMonths?: string;
      startDate?: string;
      anchorageUpperJaw?: string;
      anchorageLowerJaw?: string;
      extractUpperJawTeeth?: string;
      extractLowerJawTeeth?: string;
    } = {};

    if (!patientId) nextErrors.patientId = "Patient is required";

    // Validate notes length for braces
    if (treatmentType === "BRACES" && bracesDetails.notes.length > 500) {
      nextErrors.notes = "Notes must be 500 characters or less";
    }

    // Validate all required Braces fields
    if (treatmentType === "BRACES") {
      if (!bracesDetails.bracketType) {
        nextErrors.bracketType = "Bracket Type is required";
      }
      if (!bracesDetails.bracketMaterial) {
        nextErrors.bracketMaterial = "Select Type is required";
      }
      if (!bracesDetails.bracketSubType) {
        nextErrors.bracketSubType = "Select Sub-Type is required";
      }
      if (!bracesDetails.durationInMonths) {
        nextErrors.durationInMonths = "Duration (Months) is required";
      }
      if (!bracesDetails.startDate) {
        nextErrors.startDate = "Start Date is required";
      }
      if (!bracesDetails.anchorageUpperJaw || bracesDetails.anchorageUpperJaw.length === 0) {
        nextErrors.anchorageUpperJaw = "Anchorage Upper Jaw is required";
      }
      if (!bracesDetails.anchorageLowerJaw || bracesDetails.anchorageLowerJaw.length === 0) {
        nextErrors.anchorageLowerJaw = "Anchorage Lower Jaw is required";
      }
      if (!bracesDetails.extractUpperJawTeeth || bracesDetails.extractUpperJawTeeth.length === 0) {
        nextErrors.extractUpperJawTeeth = "Upper Jaw is required";
      }
      if (!bracesDetails.extractLowerJawTeeth || bracesDetails.extractLowerJawTeeth.length === 0) {
        nextErrors.extractLowerJawTeeth = "Lower Jaw is required";
      }
    }

    // If patient selected but no treatmentId derived, block save and show toast
    if (patientId && !treatmentId) {
      toast.error(
        "No treatments found for this patient. Please create a treatment first."
      );
      setErrors(nextErrors);
      return;
    }

    // Check if there are any validation errors
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setLoading(true);

    let dataPayload: Partial<TreatmentPlanData> = {
      id,
      patientId,
      treatmentId: treatmentId || undefined,
      type: treatmentType,
      jawType: treatmentType === "ALIGNER" ? dentalExaminationType : bracesDentalExaminationType,
    };

    if (treatmentType === "ALIGNER") {
      dataPayload.alignerDetails = alignerDetails;
    } else {
      dataPayload.bracesDetails = bracesDetails;
    }

    try {
      await createOrUpdateTreatmentPlan(dataPayload, iprFiles);
      toast.success(
        `Treatment plan ${id ? "updated" : "created"} successfully!`
      );
      navigate("/treatment-plan");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to save treatment plan."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  {id ? "Edit Treatment Plan" : "Create Treatment Plan"}
                </h2>
                <button
                  className="px-4 py-1 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>

              {/* Info message when pre-filled from treatment */}
              {isPreFilledFromTreatment && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <Lock className="w-4 h-4 text-blue-600 mr-2" />
                    <p className="text-sm text-blue-800">
                      <strong>Pre-filled from Treatment:</strong> Patient field is locked and cannot be changed.
                      Please fill in the remaining fields to complete the treatment plan.
                    </p>
                  </div>
                </div>
              )}

              <form noValidate onSubmit={handleSubmit} className="  ">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Patient <span className="text-red-500">*</span>
                      {isPreFilledFromTreatment && (
                        <span className="ml-2 text-xs text-blue-600 font-normal inline-flex items-center">
                          <Lock className="w-3 h-3 mr-1" />
                          (Pre-filled from treatment)
                        </span>
                      )}
                    </label>
                    <select
                      name="patientId"
                      value={patientId}
                      onChange={(e) => {
                        // Prevent changes to patient when pre-filled from treatment
                        if (isPreFilledFromTreatment) {
                          return;
                        }

                        const value = e.target.value;
                        setPatientId(value);

                        const selected = patients.find((p) => p.id === value);
                        if (selected?.treatmentId) {
                          setTreatmentId(String(selected.treatmentId));
                        } else {
                          setTreatmentId("");
                        }

                        setErrors((prev) => ({
                          ...prev,
                          patientId: undefined,
                          treatmentId: undefined,
                        }));
                      }}
                      aria-invalid={!!errors.patientId}
                      disabled={patientsLoading || isPreFilledFromTreatment}
                      className={`mt-1 w-full p-2 border rounded-lg ${isPreFilledFromTreatment
                        ? "bg-gray-100 cursor-not-allowed"
                        : ""
                        } ${errors.patientId
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : ""
                        }`}
                    >
                      {patientsLoading ? (
                        <option value="">Loading patients...</option>
                      ) : (
                        <>
                          <option value="" disabled>
                            Select Patient
                          </option>
                          {patients.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                    {errors.patientId && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.patientId}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Treatment Type
                    </label>
                    <select
                      value={treatmentType}
                      onChange={(e) => setTreatmentType(e.target.value as any)}
                      className="mt-1 w-full p-2 border rounded-xl"
                    >
                      <option value="ALIGNER">Aligner</option>
                      <option value="BRACES">Braces</option>
                    </select>
                  </div>
                </div>

                {treatmentType === "ALIGNER" && (
                  <AlignerForm
                    fields={alignerDetails}
                    setFields={setAlignerDetails}
                    iprFiles={iprFiles}
                    onIprChange={handleFileChange}
                    onRemoveIpr={handleRemoveImage}
                    isIprModalOpen={isIprModalOpen}
                    setIsIprModalOpen={setIsIprModalOpen}
                    prescriptionOptions={prescriptionOptions}
                    prescriptionsLoading={prescriptionsLoading}
                    existingIprDetails={existingIprDetails}
                    onDeleteExistingIpr={(id) => {
                      setSelectedIprId(id);
                      setShowIprDeleteConfirm(true);
                    }}
                    toggleAlignerUpperJawTooth={toggleAlignerUpperJawTooth}
                    toggleAlignerLowerJawTooth={toggleAlignerLowerJawTooth}
                    isAlignerUpperJawDropdownOpen={isAlignerUpperJawDropdownOpen}
                    setIsAlignerUpperJawDropdownOpen={setIsAlignerUpperJawDropdownOpen}
                    isAlignerLowerJawDropdownOpen={isAlignerLowerJawDropdownOpen}
                    setIsAlignerLowerJawDropdownOpen={setIsAlignerLowerJawDropdownOpen}
                    alignerUpperJawDropdownRef={alignerUpperJawDropdownRef}
                    alignerLowerJawDropdownRef={alignerLowerJawDropdownRef}
                    dentalExaminationType={dentalExaminationType}
                    onDentalExaminationTypeChange={handleDentalExaminationTypeChange}
                    getUpperTeethOptions={getUpperTeethOptions}
                    getLowerTeethOptions={getLowerTeethOptions}
                  />
                )}
                {treatmentType === "BRACES" && (
                  <BracesForm
                    fields={bracesDetails}
                    setFields={setBracesDetails}
                    prescriptionOptions={prescriptionOptions}
                    prescriptionsLoading={prescriptionsLoading}
                    errors={errors}
                    setErrors={setErrors}
                    selectedUpperJawTeeth={selectedUpperJawTeeth}
                    selectedLowerJawTeeth={selectedLowerJawTeeth}
                    toggleUpperJawTooth={toggleUpperJawTooth}
                    toggleLowerJawTooth={toggleLowerJawTooth}
                    isUpperJawDropdownOpen={isUpperJawDropdownOpen}
                    setIsUpperJawDropdownOpen={setIsUpperJawDropdownOpen}
                    isLowerJawDropdownOpen={isLowerJawDropdownOpen}
                    setIsLowerJawDropdownOpen={setIsLowerJawDropdownOpen}
                    upperJawDropdownRef={upperJawDropdownRef}
                    lowerJawDropdownRef={lowerJawDropdownRef}
                    bracesDentalExaminationType={bracesDentalExaminationType}
                    onBracesDentalExaminationTypeChange={handleBracesDentalExaminationTypeChange}
                    getBracesUpperTeethOptions={getBracesUpperTeethOptions}
                    getBracesLowerTeethOptions={getBracesLowerTeethOptions}
                  />
                )}

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => navigate("/treatment-plan")}
                    className="px-5 py-2 rounded-xl border"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 rounded-xl   text-white   transition"
                  >
                    {loading ? "Saving..." : "Save Plan"}
                  </button>
                 </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>

      <ConfirmModal
        isOpen={showIprDeleteConfirm}
        title="Delete IPR Image"
        message="Are you sure you want to delete this IPR image?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={() => handleDeleteExistingIpr(selectedIprId)}
        onCancel={() => {
          setShowIprDeleteConfirm(false);
          setSelectedIprId("");
        }}
        loading={loading}
        zIndex={100}
      />
    </div>
  );
};

// Sub-component for Aligner fields
type PrescriptionOption = { id: string; name: string; medicines: string[] };

const AlignerForm: React.FC<{
  fields: AlignerDetails;
  setFields: React.Dispatch<React.SetStateAction<AlignerDetails>>;
  iprFiles: File[];
  onIprChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveIpr: (index: number) => void;
  isIprModalOpen: boolean;
  setIsIprModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  prescriptionOptions: PrescriptionOption[];
  prescriptionsLoading: boolean;
  existingIprDetails: { id: string; url: string }[];
  onDeleteExistingIpr: (id: string) => void;
  toggleAlignerUpperJawTooth: (toothNumber: number) => void;
  toggleAlignerLowerJawTooth: (toothNumber: number) => void;
  isAlignerUpperJawDropdownOpen: boolean;
  setIsAlignerUpperJawDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isAlignerLowerJawDropdownOpen: boolean;
  setIsAlignerLowerJawDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  alignerUpperJawDropdownRef: React.RefObject<HTMLDivElement | null>;
  alignerLowerJawDropdownRef: React.RefObject<HTMLDivElement | null>;
  dentalExaminationType: "child" | "adult";
  onDentalExaminationTypeChange: (type: "child" | "adult") => void;
  getUpperTeethOptions: () => number[];
  getLowerTeethOptions: () => number[];
}> = ({
  fields,
  setFields,
  iprFiles,
  onIprChange,
  onRemoveIpr,
  isIprModalOpen,
  setIsIprModalOpen,
  prescriptionOptions,
  prescriptionsLoading,
  existingIprDetails,
  onDeleteExistingIpr,
  toggleAlignerUpperJawTooth,
  toggleAlignerLowerJawTooth,
  isAlignerUpperJawDropdownOpen,
  setIsAlignerUpperJawDropdownOpen,
  isAlignerLowerJawDropdownOpen,
  setIsAlignerLowerJawDropdownOpen,
  alignerUpperJawDropdownRef,
  alignerLowerJawDropdownRef,
  dentalExaminationType,
  onDentalExaminationTypeChange,
  getUpperTeethOptions,
  getLowerTeethOptions,
}) => {
    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
      const { name, value } = e.target;
      let processedValue: any = value;
      if (name === "daysToWearEachAligner" || name === "recommendedDailyHours") {
        processedValue = parseInt(value, 10) || 0;
      }
      setFields((prev) => ({ ...prev, [name]: processedValue }));
    };

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Dental & Intra Oral Examination
          </label>
          <select
            value={dentalExaminationType}
            onChange={(e) => onDentalExaminationTypeChange(e.target.value as "child" | "adult")}
            className="mt-1 w-full p-2 border rounded-xl"
          >
            {DENTAL_EXAMINATION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Days to Wear Each Aligner
          </label>
          <select
            name="daysToWearEachAligner"
            value={fields.daysToWearEachAligner}
            onChange={handleChange}
            className="mt-1 w-full p-2 border rounded-xl"
          >
            <option value={7}>7</option>
            <option value={8}>8</option>
            <option value={10}>10</option>
            <option value={12}>12</option>
            <option value={15}>15</option>
          </select>
        </div>

        <InputField
          name="recommendedDailyHours"
          label="Recommended Daily Hours"
          type="number"
          value={String(fields.recommendedDailyHours)}
          onChange={handleChange}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Upper Jaw
          </label>
          <div className="mt-1 relative" ref={alignerUpperJawDropdownRef}>
            <button
              type="button"
              onClick={() => setIsAlignerUpperJawDropdownOpen((prev) => !prev)}
              className="w-full flex flex-wrap gap-2 items-center justify-between border rounded-xl px-3 py-2 text-left"
            >
              <div className="flex flex-wrap gap-2">
                {fields.upperJawOptions.length ? (
                  fields.upperJawOptions.map((toothNumber) => (
                    <span
                      key={toothNumber}
                      className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full"
                    >
                      <span>{toothNumber}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAlignerUpperJawTooth(toothNumber);
                        }}
                        className="ml-1 text-red-500 hover:text-red-700 focus:outline-none"
                        aria-label={`Remove tooth ${toothNumber}`}
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500 text-sm">Select tooth numbers</span>
                )}
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-4 h-4 text-gray-500 transition-transform ${isAlignerUpperJawDropdownOpen ? "rotate-180" : ""
                  }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {isAlignerUpperJawDropdownOpen && (
              <div className="absolute z-20 mt-2 w-full bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {getUpperTeethOptions().map((toothNumber) => {
                  const selected = fields.upperJawOptions.includes(toothNumber);
                  return (
                    <label
                      key={toothNumber}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleAlignerUpperJawTooth(toothNumber)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{toothNumber}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Lower Jaw
          </label>
          <div className="mt-1 relative" ref={alignerLowerJawDropdownRef}>
            <button
              type="button"
              onClick={() => setIsAlignerLowerJawDropdownOpen((prev) => !prev)}
              className="w-full flex flex-wrap gap-2 items-center justify-between border rounded-xl px-3 py-2 text-left"
            >
              <div className="flex flex-wrap gap-2">
                {fields.lowerJawOptions.length ? (
                  fields.lowerJawOptions.map((toothNumber) => (
                    <span
                      key={toothNumber}
                      className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full"
                    >
                      <span>{toothNumber}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAlignerLowerJawTooth(toothNumber);
                        }}
                        className="ml-1 text-red-500 hover:text-red-700 focus:outline-none"
                        aria-label={`Remove tooth ${toothNumber}`}
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500 text-sm">Select tooth numbers</span>
                )}
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-4 h-4 text-gray-500 transition-transform ${isAlignerLowerJawDropdownOpen ? "rotate-180" : ""
                  }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {isAlignerLowerJawDropdownOpen && (
              <div className="absolute z-20 mt-2 w-full bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {getLowerTeethOptions().map((toothNumber) => {
                  const selected = fields.lowerJawOptions.includes(toothNumber);
                  return (
                    <label
                      key={toothNumber}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleAlignerLowerJawTooth(toothNumber)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{toothNumber}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* IPR uploader */}
        <div className="md:col-span-2">
          <div>
            <label className="block text-sm font-semibold text-gray-700">
              IPR Details (Images) - Max 10 images
            </label>
            <div className="mt-2 flex items-center gap-3">
              <input
                type="file"
                id="iprDetails"
                accept="image/*"
                multiple
                onChange={onIprChange}
                className="hidden"
              />
              <label
                htmlFor="iprDetails"
                className={`px-2 py-1 rounded-lg text-white cursor-pointer transition ${existingIprDetails.length + iprFiles.length >= 10
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
                  }`}
              >
                Upload Files
              </label>
              {(existingIprDetails.length + iprFiles.length) > 0 && (
                <span className={`text-sm ${existingIprDetails.length + iprFiles.length >= 10
                  ? "text-red-600 font-medium"
                  : "text-gray-600"
                  }`}>
                  {existingIprDetails.length + iprFiles.length}/10 images
                </span>
              )}
              {(existingIprDetails.length + iprFiles.length) > 0 && (
                <button
                  type="button"
                  onClick={() => setIsIprModalOpen(true)}
                  className="px-3 py-1 rounded-lg bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition"
                >
                  View images
                </button>
              )}
            </div>
          </div>
          {/* <PrescriptionList
            title="Aligner Prescriptions"
            entries={fields.prescriptions}
            onChange={(entries) =>
              setFields((prev) => ({ ...prev, prescriptions: entries }))
            }
            options={prescriptionOptions}
            loading={prescriptionsLoading}
          /> */}

          {isIprModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setIsIprModalOpen(false)}
              />
              <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">
                    IPR Images ({existingIprDetails.length + iprFiles.length}/10)
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsIprModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label="Close"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-5 h-5"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>

                {/* Existing IPR Images */}
                {existingIprDetails.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-medium text-gray-500 mb-2">
                      Existing ({existingIprDetails.length})
                    </div>
                    <ul className="space-y-2 max-h-40 overflow-auto pr-1">
                      {existingIprDetails.map((item, idx) => (
                        <li
                          key={`existing-${idx}`}
                          className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2"
                        >
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-blue-600 hover:underline truncate max-w-[300px]"
                            title={item.url}
                          >
                            Image {idx + 1}
                          </a>
                          <button
                            type="button"
                            onClick={() => onDeleteExistingIpr(item.id)}
                            className="text-gray-500 hover:text-red-600 text-sm"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <div className="text-xs font-medium text-gray-500 mb-2">
                    Selected to upload ({iprFiles.length})
                  </div>
                  {iprFiles.length === 0 ? (
                    <div className="text-sm text-gray-500">No new files selected.</div>
                  ) : (
                    <ul className="space-y-2 max-h-52 overflow-auto pr-1">
                      {iprFiles.map((file, index) => (
                        <li
                          key={`${file.name}-${index}`}
                          className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2"
                        >
                          <span
                            className="text-sm text-gray-800 truncate max-w-[360px]"
                            title={file.name}
                          >
                            File {existingIprDetails.length + index + 1} ({file.name})
                          </span>
                          <button
                            type="button"
                            onClick={() => onRemoveIpr(index)}
                            className="text-gray-500 hover:text-red-600"
                            aria-label="Remove"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsIprModalOpen(false)}
                    className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 transition"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

// Sub-component for Braces fields
const BracesForm: React.FC<{
  fields: BracesDetails;
  setFields: React.Dispatch<React.SetStateAction<BracesDetails>>;
  prescriptionOptions: PrescriptionOption[];
  prescriptionsLoading: boolean;
  errors: {
    patientId?: string;
    treatmentId?: string;
    notes?: string;
    bracketType?: string;
    bracketMaterial?: string;
    bracketSubType?: string;
    durationInMonths?: string;
    startDate?: string;
    anchorageUpperJaw?: string;
    anchorageLowerJaw?: string;
    extractUpperJawTeeth?: string;
    extractLowerJawTeeth?: string;
  };
  setErrors: React.Dispatch<React.SetStateAction<{
    patientId?: string;
    treatmentId?: string;
    notes?: string;
    bracketType?: string;
    bracketMaterial?: string;
    bracketSubType?: string;
    durationInMonths?: string;
    startDate?: string;
    anchorageUpperJaw?: string;
    anchorageLowerJaw?: string;
    extractUpperJawTeeth?: string;
    extractLowerJawTeeth?: string;
  }>>;
  selectedUpperJawTeeth: number[];
  selectedLowerJawTeeth: number[];
  toggleUpperJawTooth: (toothNumber: number) => void;
  toggleLowerJawTooth: (toothNumber: number) => void;
  isUpperJawDropdownOpen: boolean;
  setIsUpperJawDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLowerJawDropdownOpen: boolean;
  setIsLowerJawDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  upperJawDropdownRef: React.RefObject<HTMLDivElement | null>;
  lowerJawDropdownRef: React.RefObject<HTMLDivElement | null>;
  bracesDentalExaminationType: "child" | "adult";
  onBracesDentalExaminationTypeChange: (type: "child" | "adult") => void;
  getBracesUpperTeethOptions: () => number[];
  getBracesLowerTeethOptions: () => number[];
}> = ({
  fields,
  setFields,
  prescriptionOptions,
  prescriptionsLoading,
  errors,
  setErrors,
  selectedUpperJawTeeth,
  selectedLowerJawTeeth,
  toggleUpperJawTooth,
  toggleLowerJawTooth,
  isUpperJawDropdownOpen,
  setIsUpperJawDropdownOpen,
  isLowerJawDropdownOpen,
  setIsLowerJawDropdownOpen,
  upperJawDropdownRef,
  lowerJawDropdownRef,
  bracesDentalExaminationType,
  onBracesDentalExaminationTypeChange,
  getBracesUpperTeethOptions,
  getBracesLowerTeethOptions,
}) => {
    const handleChange = (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;

      // Clear field-level errors when user makes changes
      if (errors[name as keyof typeof errors]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }

      setFields((prev) => ({ ...prev, [name]: value }));
    };
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6">

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Bracket Type <span className="text-red-500">*</span>
          </label>
          <select
            name="bracketType"
            value={fields.bracketType}
            onChange={(e) => {
              handleChange(e);
              setFields({ ...fields, bracketType: e.target.value });
            }}
            className={`w-full p-2 border rounded-lg ${errors.bracketType
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : ""
              }`}
          >
            <option value="" disabled>Select Bracket Type</option>
            {BRACKET_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.bracketType && (
            <p className="text-red-500 text-sm mt-1">{errors.bracketType}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Select Type <span className="text-red-500">*</span>
          </label>
          <select
            name="bracketMaterial"
            value={fields.bracketMaterial}
            onChange={(e) => {
              handleChange(e);
              setFields({ ...fields, bracketMaterial: e.target.value });
            }}
            className={`w-full p-2 border rounded-lg ${errors.bracketMaterial
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : ""
              }`}
          >
            <option value="" disabled>Select Type</option>
            {BRACKET_MATERIAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.bracketMaterial && (
            <p className="text-red-500 text-sm mt-1">{errors.bracketMaterial}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Select Sub-Type <span className="text-red-500">*</span>
          </label>
          <select
            name="bracketSubType"
            value={fields.bracketSubType}
            onChange={(e) => {
              handleChange(e);
              setFields({ ...fields, bracketSubType: e.target.value });
            }}
            className={`w-full p-2 border rounded-lg ${errors.bracketSubType
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : ""
              }`}
          >
            <option value="" disabled>Select Sub-Type</option>
            {BRACKET_SUBTYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.bracketSubType && (
            <p className="text-red-500 text-sm mt-1">{errors.bracketSubType}</p>
          )}
        </div>


        <div>
          <label className="block text-sm font-medium text-gray-700">
            Duration (Months) <span className="text-red-500">*</span>
          </label>
          <select
            name="durationInMonths"
            value={fields.durationInMonths || ""}
            onChange={handleChange}
            className={`mt-1 w-full p-2 border rounded-xl bg-white ${errors.durationInMonths
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : ""
              }`}
          >
            <option value="" disabled>
              Select duration
            </option>
            {BRACES_DURATION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {errors.durationInMonths && (
            <p className="text-red-500 text-sm mt-1">{errors.durationInMonths}</p>
          )}
        </div>
        <InputField
          name="startDate"
          label="Start Date"
          type="date"
          value={fields.startDate.split("T")[0]}
          onChange={handleChange}
          required={true}
          error={errors.startDate}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Anchorage Upper Jaw <span className="text-red-500">*</span>
          </label>
          <select
            name="anchorageUpperJaw"
            value={fields.anchorageUpperJaw[0] || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (errors.anchorageUpperJaw) {
                setErrors((prev) => ({ ...prev, anchorageUpperJaw: undefined }));
              }
              setFields((prev) => ({
                ...prev,
                anchorageUpperJaw: value ? [value] : [],
              }));
            }}
            className={`mt-1 w-full p-2 border rounded-xl bg-white ${errors.anchorageUpperJaw
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : ""
              }`}
          >
            <option value="" disabled>
              Select anchorage option
            </option>
            {DEFAULT_ANCHORAGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.anchorageUpperJaw && (
            <p className="text-red-500 text-sm mt-1">{errors.anchorageUpperJaw}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Anchorage Lower Jaw <span className="text-red-500">*</span>
          </label>
          <select
            name="anchorageLowerJaw"
            value={fields.anchorageLowerJaw[0] || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (errors.anchorageLowerJaw) {
                setErrors((prev) => ({ ...prev, anchorageLowerJaw: undefined }));
              }
              setFields((prev) => ({
                ...prev,
                anchorageLowerJaw: value ? [value] : [],
              }));
            }}
            className={`mt-1 w-full p-2 border rounded-xl bg-white ${errors.anchorageLowerJaw
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : ""
              }`}
          >
            <option value="" disabled>
              Select anchorage option
            </option>
            {DEFAULT_ANCHORAGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.anchorageLowerJaw && (
            <p className="text-red-500 text-sm mt-1">{errors.anchorageLowerJaw}</p>
          )}
        </div>
        {/* Tooth Selection UI for Braces */}
        <div className="md:col-span-2 border-2 border-gray-200 rounded-2xl p-6 bg-gray-50">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-3">
              Select tooth for extraction
            </h3>
            <div className="flex bg-gray-100 p-1 rounded-xl">
              {DENTAL_EXAMINATION_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onBracesDentalExaminationTypeChange(option.value as "child" | "adult")}
                  className={`px-6 py-1.5 rounded-lg text-sm font-medium transition-all ${bracesDentalExaminationType === option.value
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Upper Jaw Grid */}
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-gray-700">
                Upper jaw <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col gap-3 overflow-x-auto pb-2">
                {/* Row 1 */}
                <div className="flex gap-2 min-w-max">
                  {bracesDentalExaminationType === "child"
                    ? [51, 52, 53, 54, 55].map((num) => {
                      const isSelected = selectedUpperJawTeeth.includes(num);
                      return (
                        <button
                          key={num}
                          type="button"
                          onClick={() => toggleUpperJawTooth(num)}
                          className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${isSelected
                            ? "bg-gray-200 border-gray-300 text-gray-700"
                            : "bg-cyan-100 border-cyan-200 text-cyan-700"
                            }`}
                        >
                          {num}
                        </button>
                      );
                    })
                    : [11, 12, 13, 14, 15, 16, 17, 18].map((num) => {
                      const isSelected = selectedUpperJawTeeth.includes(num);
                      return (
                        <button
                          key={num}
                          type="button"
                          onClick={() => toggleUpperJawTooth(num)}
                          className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${isSelected
                            ? "bg-gray-200 border-gray-300 text-gray-700"
                            : "bg-cyan-100 border-cyan-200 text-cyan-700"
                            }`}
                        >
                          {num}
                        </button>
                      );
                    })}
                </div>
                {/* Row 2 */}
                <div className="flex gap-2 min-w-max">
                  {bracesDentalExaminationType === "child"
                    ? [61, 62, 63, 64, 65].map((num) => {
                      const isSelected = selectedUpperJawTeeth.includes(num);
                      return (
                        <button
                          key={num}
                          type="button"
                          onClick={() => toggleUpperJawTooth(num)}
                          className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${isSelected
                            ? "bg-gray-200 border-gray-300 text-gray-700"
                            : "bg-cyan-100 border-cyan-200 text-cyan-700"
                            }`}
                        >
                          {num}
                        </button>
                      );
                    })
                    : [21, 22, 23, 24, 25, 26, 27, 28].map((num) => {
                      const isSelected = selectedUpperJawTeeth.includes(num);
                      return (
                        <button
                          key={num}
                          type="button"
                          onClick={() => toggleUpperJawTooth(num)}
                          className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${isSelected
                            ? "bg-gray-200 border-gray-300 text-gray-700"
                            : "bg-cyan-100 border-cyan-200 text-cyan-700"
                            }`}
                        >
                          {num}
                        </button>
                      );
                    })}
                </div>
              </div>
              {errors.extractUpperJawTeeth && (
                <p className="text-red-500 text-sm mt-2">{errors.extractUpperJawTeeth}</p>
              )}
            </div>

            {/* Lower Jaw Grid */}
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-gray-700">
                Lower jaw <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col gap-3 overflow-x-auto pb-2">
                {/* Row 1 */}
                <div className="flex gap-2 min-w-max">
                  {bracesDentalExaminationType === "child"
                    ? [71, 72, 73, 74, 75].map((num) => {
                      const isSelected = selectedLowerJawTeeth.includes(num);
                      return (
                        <button
                          key={num}
                          type="button"
                          onClick={() => toggleLowerJawTooth(num)}
                          className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${isSelected
                            ? "bg-gray-200 border-gray-300 text-gray-700"
                            : "bg-cyan-100 border-cyan-200 text-cyan-700"
                            }`}
                        >
                          {num}
                        </button>
                      );
                    })
                    : [31, 32, 33, 34, 35, 36, 37, 38].map((num) => {
                      const isSelected = selectedLowerJawTeeth.includes(num);
                      return (
                        <button
                          key={num}
                          type="button"
                          onClick={() => toggleLowerJawTooth(num)}
                          className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${isSelected
                            ? "bg-gray-200 border-gray-300 text-gray-700"
                            : "bg-cyan-100 border-cyan-200 text-cyan-700"
                            }`}
                        >
                          {num}
                        </button>
                      );
                    })}
                </div>
                {/* Row 2 */}
                <div className="flex gap-2 min-w-max">
                  {bracesDentalExaminationType === "child"
                    ? [81, 82, 83, 84, 85].map((num) => {
                      const isSelected = selectedLowerJawTeeth.includes(num);
                      return (
                        <button
                          key={num}
                          type="button"
                          onClick={() => toggleLowerJawTooth(num)}
                          className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${isSelected
                            ? "bg-gray-200 border-gray-300 text-gray-700"
                            : "bg-cyan-100 border-cyan-200 text-cyan-700"
                            }`}
                        >
                          {num}
                        </button>
                      );
                    })
                    : [41, 42, 43, 44, 45, 46, 47, 48].map((num) => {
                      const isSelected = selectedLowerJawTeeth.includes(num);
                      return (
                        <button
                          key={num}
                          type="button"
                          onClick={() => toggleLowerJawTooth(num)}
                          className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg border text-sm font-bold transition-all shadow-sm ${isSelected
                            ? "bg-gray-200 border-gray-300 text-gray-700"
                            : "bg-cyan-100 border-cyan-200 text-cyan-700"
                            }`}
                        >
                          {num}
                        </button>
                      );
                    })}
                </div>
              </div>
              {errors.extractLowerJawTeeth && (
                <p className="text-red-500 text-sm mt-2">{errors.extractLowerJawTeeth}</p>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 text-sm mt-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-cyan-100 border border-cyan-200 rounded"></div>
              <span className="text-gray-600">Present</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 border border-gray-300 rounded"></div>
              <span className="text-gray-600">Selected for extraction</span>
            </div>
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">
            Notes
          </label>
          <textarea
            name="notes"
            value={fields.notes}
            onChange={handleChange}
            rows={3}
            maxLength={500}
            className={`mt-1 w-full p-2 border rounded-xl ${errors.notes
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : ""
              }`}
            placeholder="Enter treatment notes (max 500 characters)"
          />
          <div className="flex justify-between items-center mt-1">
            <div>
              {errors.notes && (
                <p className="text-red-500 text-sm">{errors.notes}</p>
              )}
            </div>
            <div className={`text-sm ${fields.notes.length > 480
              ? fields.notes.length > 490
                ? "text-red-500 font-medium"
                : "text-orange-500"
              : "text-gray-500"
              }`}>
              {fields.notes.length}/500 characters
            </div>
          </div>
        </div>
        {/* <PrescriptionList
        title="Braces Prescriptions"
        entries={fields.prescriptions}
        onChange={(entries) =>
          setFields((prev) => ({ ...prev, prescriptions: entries }))
        }
        options={prescriptionOptions}
        loading={prescriptionsLoading}
      /> */}
      </div>
    );
  };

const PrescriptionList: React.FC<{
  title: string;
  entries: TreatmentPlanPrescription[];
  onChange: (next: TreatmentPlanPrescription[]) => void;
  options: PrescriptionOption[];
  loading: boolean;
}> = ({ title, entries, onChange, options, loading }) => {
  const handleEntryChange = (
    index: number,
    key: keyof TreatmentPlanPrescription,
    value: string
  ) => {
    onChange(
      entries.map((entry, idx) =>
        idx === index
          ? {
            ...entry,
            [key]:
              key === "medications"
                ? value
                  .split(",")
                  .map((med) => med.trim())
                  .filter((med) => med.length > 0)
                : value,
          }
          : entry
      )
    );
  };

  const addEntry = () => {
    onChange([
      ...entries,
      {
        prescriptionId: "",
        medications: [],
      },
    ]);
  };

  const removeEntry = (index: number) => {
    onChange(entries.filter((_, idx) => idx !== index));
  };

  const handleSelectChange = (index: number, prescriptionId: string) => {
    const selected = options.find((opt) => opt.id === prescriptionId);
    onChange(
      entries.map((entry, idx) =>
        idx === index
          ? {
            ...entry,
            prescriptionId,
            medications: selected ? [...selected.medicines] : [],
          }
          : entry
      )
    );
  };

  return (
    <div className="md:col-span-2 rounded-2xl border border-dashed border-gray-200 p-4 bg-gray-50 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
        <button
          type="button"
          onClick={addEntry}
          className="text-sm px-3 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
        >
          + Add
        </button>
      </div>
      {loading && (
        <p className="text-sm text-gray-500">Loading prescriptions...</p>
      )}
      {!loading && options.length === 0 && (
        <p className="text-sm text-yellow-600">
          No prescriptions available. Create one first.
        </p>
      )}
      {entries.length === 0 ? (
        <p className="text-sm text-gray-500">No prescriptions added yet.</p>
      ) : (
        <div className="space-y-4">
          {entries.map((entry, index) => (
            <div
              key={`prescription-${index}`}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white rounded-xl border border-gray-200 p-4"
            >
              <div>
                <label className="block text-xs font-medium text-gray-600">
                  Prescription
                </label>
                <select
                  value={entry.prescriptionId}
                  onChange={(e) => handleSelectChange(index, e.target.value)}
                  disabled={options.length === 0}
                  className="mt-1 w-full p-2 border rounded-lg"
                >
                  <option value="">Select Prescription</option>
                  {options.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">
                  Medications (comma separated)
                </label>
                <input
                  type="text"
                  value={entry.medications.join(", ")}
                  onChange={(e) =>
                    handleEntryChange(index, "medications", e.target.value)
                  }
                  className="mt-1 w-full p-2 border rounded-lg"
                />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeEntry(index)}
                  className="text-sm text-red-600 hover:underline"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Simple InputField component to reduce repetition
const InputField: React.FC<{
  name: string;
  label: string;
  value: string;
  onChange: (e: any) => void;
  type?: string;
  required?: boolean;
  error?: string;
}> = ({ name, label, value, onChange, type = "text", required = false, error }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className={`mt-1 w-full p-2 border rounded-xl ${error
        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
        : ""
        }`}
    />
    {error && (
      <p className="text-red-500 text-sm mt-1">{error}</p>
    )}
  </div>
);

type OptionItem = { label: string; value: string };

const MultiSelectDropdown: React.FC<{
  label: string;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  options?: OptionItem[];
  placeholder?: string;
  helperText?: string;
  allowCustomEntry?: boolean;
  singleSelect?: boolean;
}> = ({
  label,
  selectedValues,
  onChange,
  options = [],
  placeholder = "Select options",
  helperText,
  allowCustomEntry = false,
  singleSelect = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [customValue, setCustomValue] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const toggleValue = (value: string) => {
      if (singleSelect) {
        onChange(
          selectedValues.includes(value) ? [] : [value]
        );
        return;
      }

      onChange(
        selectedValues.includes(value)
          ? selectedValues.filter((item) => item !== value)
          : [...selectedValues, value]
      );
    };

    const handleAddCustom = () => {
      const trimmed = customValue.trim();
      if (!trimmed) return;
      if (singleSelect) {
        onChange([trimmed]);
      } else if (!selectedValues.includes(trimmed)) {
        onChange([...selectedValues, trimmed]);
      }
      setCustomValue("");
    };

    const handleCustomKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleAddCustom();
      }
    };

    const handleChipRemove = (
      event: React.MouseEvent<HTMLButtonElement>,
      value: string
    ) => {
      event.stopPropagation();
      onChange(selectedValues.filter((item) => item !== value));
    };

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <div className="mt-1 relative" ref={containerRef}>
          <button
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            className="w-full flex flex-wrap gap-2 items-center justify-between border rounded-xl px-3 py-2 text-left"
          >
            <div className="flex flex-wrap gap-2">
              {selectedValues.length ? (
                selectedValues.map((value) => (
                  <span
                    key={value}
                    className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full"
                  >
                    <span>{value}</span>
                    <button
                      type="button"
                      onClick={(e) => handleChipRemove(e, value)}
                      className="ml-1 text-red-500 hover:text-red-700 focus:outline-none"
                      aria-label={`Remove ${value}`}
                    >
                      ×
                    </button>
                  </span>
                ))
              ) : (
                <span className="text-gray-500 text-sm">{placeholder}</span>
              )}
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""
                }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isOpen && (
            <div className="absolute z-20 mt-2 w-full bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
              {options.length > 0 &&
                options.map((option) => {
                  const selected = selectedValues.includes(option.value);
                  return (
                    <label
                      key={option.value}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleValue(option.value)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{option.label}</span>
                    </label>
                  );
                })}
              {allowCustomEntry && (
                <div className="border-t px-3 py-2 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">
                    Type and press enter to add custom values
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customValue}
                      onChange={(e) => setCustomValue(e.target.value)}
                      onKeyDown={handleCustomKey}
                      className="flex-1 border rounded-lg px-2 py-1 text-sm"
                      placeholder="Add custom entry"
                    />
                    <button
                      type="button"
                      onClick={handleAddCustom}
                      className="px-3 py-1 text-sm rounded-lg bg-blue-600 text-white"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {helperText && (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    );
  };

export default TreatmentPlanForm;
